import { NexusError, ProtocolError, RateLimitError, TimeoutError, } from "@nexusmods/nexus-api";
import React from "react";
import { IconButton } from "../../../controls/TooltipControls";
import { ProcessCanceled } from "../../../util/CustomErrors";
import { laterT } from "../../../util/i18n";
import { log } from "../../../util/log";
import { activeGameId, gameById } from "../../../util/selectors";
import { getSafe } from "../../../util/storeHelper";
import { truthy } from "../../../util/util";
import { getGame } from "../../gamemode_management/util/getGame";
import { nexusGameId } from "./convertGameId";
class Tracking {
    mApi;
    mNexus;
    mOnChanged;
    mTrackedMods = {};
    constructor(api) {
        this.mApi = api;
    }
    once(nexusInstance) {
        this.mNexus = nexusInstance;
        const state = this.mApi.getState();
        const username = getSafe(state, ["persistent", "nexus", "userInfo", "name"], undefined);
        if (username !== undefined) {
            this.fetch();
        }
        this.mApi.onStateChange(["persistent", "nexus", "userInfo"], (oldUserInfo, newUserInfo) => {
            if (newUserInfo === undefined) {
                this.mTrackedMods = {};
                this.mOnChanged?.();
            }
            else {
                this.fetch();
            }
        });
    }
    attribute() {
        const TrackedIcon = this.makeIcon();
        return {
            id: "tracked",
            name: laterT("Tracking"),
            description: laterT("Tracked on Nexus"),
            icon: "track",
            customRenderer: (mod, detail, t) => mod.attributes?.source === "nexus" ? (React.createElement(TrackedIcon, { t: t, mod: mod })) : null,
            calc: (mod) => {
                if (mod.attributes?.source === "nexus") {
                    const gameMode = activeGameId(this.mApi.getState());
                    const nexusId = nexusGameId(getGame(gameMode));
                    if (!truthy(mod.attributes?.modId)) {
                        return false;
                    }
                    return this.mTrackedMods[nexusId]?.has?.(mod.attributes?.modId.toString());
                }
                else {
                    return undefined;
                }
            },
            externalData: (changeCB) => {
                this.mOnChanged = changeCB;
            },
            placement: "table",
            isToggleable: true,
            isDefaultVisible: false,
            edit: {},
            isSortable: true,
        };
    }
    trackMods(modIds) {
        const state = this.mApi.getState();
        const gameMode = activeGameId(state);
        const mods = state.persistent.mods[gameMode] ?? {};
        const downloads = state.persistent.downloads.files;
        const game = gameById(state, gameMode);
        const nexusId = nexusGameId(game);
        modIds.forEach((modId) => {
            if (truthy(mods[modId]?.attributes?.modId)) {
                this.trackMod(nexusId, mods[modId].attributes.modId.toString?.());
            }
            else if (truthy(downloads[modId]?.modInfo?.nexus?.ids?.modId)) {
                this.trackMod(nexusId, downloads[modId].modInfo.nexus.ids.modId.toString());
            }
        });
    }
    untrackMods(modIds) {
        const state = this.mApi.getState();
        const gameMode = activeGameId(state);
        const mods = state.persistent.mods[gameMode] ?? {};
        const downloads = state.persistent.downloads.files;
        const game = gameById(state, gameMode);
        const nexusId = nexusGameId(game);
        modIds.forEach((modId) => {
            if (truthy(mods[modId]?.attributes?.modId)) {
                this.untrackMod(nexusId, mods[modId].attributes.modId.toString?.());
            }
            else if (truthy(downloads[modId]?.modInfo?.nexus?.ids?.modId)) {
                this.untrackMod(nexusId, downloads[modId].modInfo.nexus.ids.modId.toString());
            }
        });
    }
    makeIcon() {
        return (props) => {
            const { t, mod } = props;
            const gameMode = activeGameId(this.mApi.getState());
            if (mod.attributes?.modId === undefined) {
                return null;
            }
            const nexusId = nexusGameId(getGame(gameMode));
            return (React.createElement(IconButton, { icon: "track", className: "btn-embed", stroke: !this.mTrackedMods[nexusId]?.has?.(mod.attributes?.modId?.toString()), hollow: !this.mTrackedMods[nexusId]?.has?.(mod.attributes?.modId?.toString()), tooltip: t("Mod Tracked"), "data-modid": mod.attributes?.modId, onClick: this.toggleTracked }));
        };
    }
    fetch() {
        if (this.mNexus.getValidationResult() === null) {
            return;
        }
        this.mNexus
            .getTrackedMods()
            .then((tracked) => {
            this.mTrackedMods = tracked.reduce((prev, iter) => {
                if (prev[iter.domain_name] === undefined) {
                    prev[iter.domain_name] = new Set();
                }
                prev[iter.domain_name].add(iter.mod_id.toString());
                return prev;
            }, {});
            this.mOnChanged?.();
        })
            .catch((err) => {
            if (err instanceof ProcessCanceled) {
                return;
            }
            const allowReport = !(err instanceof RateLimitError) &&
                !(err instanceof TimeoutError) &&
                !(err instanceof ProtocolError) &&
                !(err instanceof NexusError);
            this.mApi.showErrorNotification("Failed to get list of tracked mods", err, {
                allowReport,
            });
        });
    }
    toggleTracked = (evt) => {
        const state = this.mApi.getState();
        const gameMode = activeGameId(state);
        const modIdStr = evt.currentTarget.getAttribute("data-modid");
        if (state.persistent["nexus"]?.userInfo === undefined) {
            // user not logged in
            this.mApi.sendNotification({
                type: "warning",
                message: "You have to be logged in to track mods",
                displayMS: 5000,
            });
            return;
        }
        const game = gameById(state, gameMode);
        const nexusId = nexusGameId(game);
        if (this.mTrackedMods[nexusId]?.has?.(modIdStr)) {
            this.untrackMod(nexusId, modIdStr);
        }
        else {
            this.trackMod(nexusId, modIdStr);
        }
    };
    trackMod(nexusId, nexusModId) {
        if (this.mTrackedMods[nexusId]?.has?.(nexusModId)) {
            return Promise.resolve();
        }
        return this.mNexus
            .trackMod(nexusModId, nexusId)
            .then(() => {
            if (this.mTrackedMods[nexusId] === undefined) {
                this.mTrackedMods[nexusId] = new Set();
            }
            this.mTrackedMods[nexusId].add(nexusModId);
            this.mOnChanged?.();
        })
            .catch((err) => {
            this.mApi.showErrorNotification("Failed to track/untrack mod", err, {
                allowReport: err instanceof RateLimitError,
            });
        });
    }
    untrackMod(nexusId, nexusModId) {
        if (!this.mTrackedMods[nexusId]?.has?.(nexusModId)) {
            return Promise.resolve();
        }
        return this.mNexus
            .untrackMod(nexusModId, nexusId)
            .then(() => {
            this.mTrackedMods[nexusId]?.delete?.(nexusModId);
            this.mOnChanged?.();
        })
            .catch((err) => {
            if (err["statusCode"] === 404) {
                // user isn't actually tracking the mod
                log("warn", "mod tracking state out of sync between server and Vortex", { game: nexusId, modId: nexusModId });
                this.mTrackedMods[nexusId]?.delete?.(nexusModId);
                this.mOnChanged?.();
            }
            else {
                this.mApi.showErrorNotification("Failed to track/untrack mod", err);
            }
        });
    }
}
export default Tracking;
